import { useState, useEffect } from "react";

import { Box, Flex, Text, HStack, Button, NativeSelect } from "@libs/design";
import { Filter, Download } from "@assets/icons";

import { useExportCountries } from "../../api";

export type CountriesFilters = {
  region: string;
  status: string;
  domain: string;
  month: string;
  year: string;
};

export type FilterBarProps = {
  onFilterChange?: (filters: CountriesFilters) => void;
  availableRegions?: string[];
  availableDomains?: string[];
  availableMonths?: string[];
  availableYears?: (number | string)[];
};
const ALL_REGIONS = "All Regions";
const ALL_STATUS = "All Status";
const DEFAULT_DOMAIN = "Purchase";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Walk back from the current calendar month until we find one present in availableMonths. */
function resolveLatestMonth(availableMonths: string[]): string {
  const currentIdx = new Date().getMonth(); // 0 = January
  for (let i = 0; i < 12; i++) {
    const candidate = MONTHS[(currentIdx - i + 12) % 12];
    if (availableMonths.includes(candidate)) return candidate;
  }
  return availableMonths[availableMonths.length - 1] ?? "";
}

export const FilterBar = ({
  onFilterChange,
  availableRegions = [],
  availableDomains = [],
  availableMonths = [],
  availableYears = [],
}: FilterBarProps) => {
  const [region, setRegion] = useState(ALL_REGIONS);
  const [status, setStatus] = useState(ALL_STATUS);
  const [domain, setDomain] = useState(DEFAULT_DOMAIN);
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  // Auto-select latest month and year when options first arrive from the API
  useEffect(() => {
    if (!availableMonths.length && !availableYears.length) return;
    let newMonth = month;
    let newYear = year;
    if (availableMonths.length && !availableMonths.includes(month)) {
      newMonth = resolveLatestMonth(availableMonths);
    }
    if (availableYears.length && !availableYears.map(String).includes(year)) {
      // years are sorted descending, so first entry is latest
      newYear = String(availableYears[0]);
    }
    if (newMonth !== month) setMonth(newMonth);
    if (newYear !== year) setYear(newYear);
    if (newMonth !== month || newYear !== year) {
      onFilterChange?.({ region, status, domain, month: newMonth, year: newYear });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMonths, availableYears]);

  const colors = {
    textPrimary: "#111827",
    textSecondary: "#6B7280",
    border: "#E5E7EB",
    icon: "#6B7280",
  };

  const notify = (r: string, s: string, d: string, m: string, y: string) => {
    onFilterChange?.({ region: r, status: s, domain: d, month: m, year: y });
  };

  const handleRegionChange = (value: string) => {
    setRegion(value);
    notify(value, status, domain, month, year);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value);
    notify(region, value, domain, month, year);
  };

  const handleDomainChange = (value: string) => {
    setDomain(value);
    notify(region, status, value, month, year);
  };

  const handleMonthChange = (value: string) => {
    setMonth(value);
    notify(region, status, domain, value, year);
  };

  const handleYearChange = (value: string) => {
    setYear(value);
    notify(region, status, domain, month, value);
  };
  const { exportCountries } = useExportCountries();

  return (
    <Box
      bg="white"
      borderRadius="xl"
      borderWidth="1px"
      p={{ base: 3, md: 4 }}
      boxShadow="sm"
      style={{ borderColor: colors.border }}
    >
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        gap={{ base: 4, md: 0 }}
      >
        {/* Filters Section */}
        <HStack gap={3} flexWrap="wrap" align="center">
          {/* Label */}
          <HStack gap={2} w={{ base: "full", md: "auto" }}>
            <Box as="span" style={{ color: colors.icon }} display="inline-flex" alignItems="center">
              <Filter size={16} />
            </Box>
            <Text fontSize="sm" style={{ color: colors.textSecondary }}>
              Filter by:
            </Text>
          </HStack>

          {/* Region (subregion) - All Regions = exclude ALL value */}
          <NativeSelect.Root
            fontSize="sm"
            borderWidth="1px"
            borderRadius="lg"
            bg="white"
            w={{ base: "full", sm: 40 }}
            style={{ borderColor: colors.border }}
          >
            <NativeSelect.Field value={region} onChange={(e) => handleRegionChange(e.target.value)}>
              <option value={ALL_REGIONS}>{ALL_REGIONS}</option>
              {availableRegions
                .filter((r) => r !== ALL_REGIONS && r.toUpperCase() !== "ALL")
                .map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
            </NativeSelect.Field>
          </NativeSelect.Root>

          {/* Status */}
          <NativeSelect.Root
            fontSize="sm"
            borderWidth="1px"
            borderRadius="lg"
            bg="white"
            w={{ base: "full", sm: 32 }}
            style={{ borderColor: colors.border }}
          >
            <NativeSelect.Field value={status} onChange={(e) => handleStatusChange(e.target.value)}>
              <option value={ALL_STATUS}>{ALL_STATUS}</option>
              <option value="Healthy">Healthy</option>
              <option value="Moderate">Moderate</option>
              <option value="Critical">Critical</option>
            </NativeSelect.Field>
          </NativeSelect.Root>

          {/* Domain (source) - All Domains = exclude ALL value */}
          <NativeSelect.Root
            fontSize="sm"
            borderWidth="1px"
            borderRadius="lg"
            bg="white"
            w={{ base: "full", sm: 32 }}
            style={{ borderColor: colors.border }}
          >
            <NativeSelect.Field value={domain} onChange={(e) => handleDomainChange(e.target.value)}>
              {availableDomains
                .filter((d) => d.toUpperCase() !== "ALL")
                .map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
            </NativeSelect.Field>
          </NativeSelect.Root>

          {/* Month */}
          <NativeSelect.Root
            fontSize="sm"
            borderWidth="1px"
            borderRadius="lg"
            bg="white"
            w={{ base: "full", sm: 36 }}
            style={{ borderColor: colors.border }}
          >
            <NativeSelect.Field value={month} onChange={(e) => handleMonthChange(e.target.value)}>
              {availableMonths.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </NativeSelect.Field>
          </NativeSelect.Root>

          {/* Year */}
          <NativeSelect.Root
            fontSize="sm"
            borderWidth="1px"
            borderRadius="lg"
            bg="white"
            w={{ base: "full", sm: 28 }}
            style={{ borderColor: colors.border }}
          >
            <NativeSelect.Field value={year} onChange={(e) => handleYearChange(e.target.value)}>
              {availableYears.map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </NativeSelect.Field>
          </NativeSelect.Root>
        </HStack>

        {/* Export Section */}
        <HStack mt={{ base: 2, md: 0 }} justify={{ base: "flex-start", md: "flex-end" }}>
          <Box as="span" style={{ color: colors.icon }} display="inline-flex" alignItems="center">
            <Download size={16} color="#00927B" />
          </Box>
          <Button
            size={{ base: "md", md: "lg" }}
            variant="outline"
            onClick={() =>
              exportCountries({
                region,
                status,
                domain,
              })
            }
          >
            Export
          </Button>
        </HStack>
      </Flex>
    </Box>
  );
};
